import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./CompanyAtomikaPage.module.scss";

export default function CompanyAtomikaPage() {
    const t = useTranslations("CompanyAtomikaPage");

    const [heroRef, heroInView] = useInView();
    const [ideaRef, ideaInView] = useInView();
    const [previewRef, previewInView] = useInView();
    const [systemRef, systemInView] = useInView();
    const [anomalyRef, anomalyInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Atomika - " + WEBPAGE_TITLE}</title>
                <meta
                    name="keywords"
                    content="Atomika, hitakerfi, heitt vatn, frávik, orka, rekstur"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Atomika`}
                    key="og:title"
                />
                <meta
                    property="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <section
                    ref={ideaRef}
                    className={`${styles.idea} ${styles.revealTarget} ${ideaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.ideaInner}>
                        <div className={styles.ideaCard}>
                            <span className={styles.ideaLabel}>
                                {t("idea_orka_label")}
                            </span>
                            <p className={styles.ideaText}>{t("idea_orka")}</p>
                        </div>
                        <div
                            className={`${styles.ideaCard} ${styles.ideaCardAccent}`}
                        >
                            <span className={styles.ideaLabel}>
                                {t("idea_atomika_label")}
                            </span>
                            <p className={styles.ideaText}>
                                {t("idea_atomika")}
                            </p>
                        </div>
                        <div className={styles.ideaCard}>
                            <span className={styles.ideaLabel}>
                                {t("idea_anomaly_label")}
                            </span>
                            <p className={styles.ideaText}>
                                {t("idea_anomaly")}
                            </p>
                        </div>
                    </div>
                </section>

                <section
                    ref={previewRef}
                    className={`${styles.preview} ${styles.revealTarget} ${previewInView ? styles.revealed : ""}`}
                >
                    <div className={styles.previewInner}>
                        <span className={styles.previewLabel}>
                            {t("preview_label")}
                        </span>
                        <h2 className={styles.previewTitle}>
                            {t("preview_title")}
                        </h2>
                        <p className={styles.previewDesc}>
                            {t("preview_desc")}
                        </p>
                        <figure className={styles.browserFrame}>
                            <div className={styles.browserBar}>
                                <span className={styles.browserDot} />
                                <span className={styles.browserDot} />
                                <span className={styles.browserDot} />
                            </div>
                            <img
                                className={styles.browserImg}
                                src="/img/atomika-dashboard.png"
                                alt={t("preview_caption")}
                            />
                        </figure>
                        <p className={styles.previewCaption}>
                            {t("preview_caption")}
                        </p>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={systemRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${systemInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_system_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_system_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_system_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M3 12h4l2 5 4-14 2 9h6" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("kpi_flow")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("kpi_flow_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("kpi_supply")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("kpi_supply_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="12"
                                                y1="19"
                                                x2="12"
                                                y2="5"
                                            />
                                            <polyline points="5 12 12 5 19 12" />
                                            <polyline
                                                points="5 19 12 12 19 19"
                                                opacity="0.5"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("kpi_delta")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("kpi_delta_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("kpi_load")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("kpi_load_desc")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={anomalyRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${anomalyInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_anomaly_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_anomaly_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_anomaly_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.resultStack}>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                            <line
                                                x1="12"
                                                y1="9"
                                                x2="12"
                                                y2="13"
                                            />
                                            <line
                                                x1="12"
                                                y1="17"
                                                x2="12.01"
                                                y2="17"
                                            />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_detect")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_detect_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_explain")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_explain_desc")}
                                        </span>
                                    </div>
                                </div>
                                <div className={styles.resultCard}>
                                    <div className={styles.resultIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                                            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                                        </svg>
                                    </div>
                                    <div className={styles.resultContent}>
                                        <span className={styles.resultLabel}>
                                            {t("result_notify")}
                                        </span>
                                        <span className={styles.resultMeta}>
                                            {t("result_notify_desc")}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
